import Card from "@/components/Card";
import InfoText from "@/components/InfoText";
import { Button } from "@/components/ui/button";
import { handleCompanyName } from "@/lib/handleName";
import { Company } from "@/schema/Company";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

const EMPTY_PLACEHOLDER = "--";

interface Props {
  showDetail?: boolean;
  company: Company;
  onEdit?: () => void;
}

function CompanyCard({
  showDetail = true,
  company: {
    id,
    ceo_name,
    ceo_name_kana,
    company_name,
    company_name_kana,
    email,
    position,
    market_name,
    employee_count,
    foreign_employee_count,
    permanent_employee_count,
    contract_employee_count,
    temporary_employee_count,
    contractor_count,
    part_time_employee_count,
    intern_count,
    specialist_count,
    employee_countries,
    funds,
    is_listed,
    company_address,
    corporate_number,
    registration_number,
    established_date,
    homepage_url,
    phone_number,
  },
  onEdit,
}: Props) {
  const { t } = useTranslation();

  return (
    <Card>
      <div className="grid grid-cols-2 gap-4 p-6 border-b border-cool-gray-200">
        <InfoText
          label={t("company-name")}
          text={handleCompanyName(id, company_name)}
        />
        <InfoText label={t("company-name-kana")} text={company_name_kana} />
        <InfoText label={t("representative-name")} text={ceo_name} />
        <InfoText label={t("representative-name-kana")} text={ceo_name_kana} />
        <InfoText label={t("phone")} text={phone_number} />
        <InfoText label={t("email")} text={email} />
        <InfoText label={t("location")} text={company_address} />
        <InfoText
          label={t("company-website")}
          link={homepage_url}
          text={homepage_url}
        />
      </div>
      <div className="grid grid-cols-2 gap-4 p-6 border-b border-cool-gray-200">
        <InfoText label={t("registration-number")} text={registration_number} />
        <InfoText label={t("corporate-number")} text={corporate_number} />
      </div>
      {showDetail && (
        <>
          <div className="grid grid-cols-2 gap-4 p-6 border-t border-cool-gray-200">
            <InfoText
              label={t("listed-market")}
              text={is_listed ? t("listed") : EMPTY_PLACEHOLDER}
            />
            <InfoText label={t("market-name")} text={market_name} />
            <InfoText
              label={t("established-date")}
              text={
                established_date
                  ? format(established_date, t("year-month-format"))
                  : EMPTY_PLACEHOLDER
              }
            />
            <InfoText
              label={t("capital")}
              text={funds ? String(funds) : EMPTY_PLACEHOLDER}
            />
          </div>
          <div className="grid grid-cols-2 gap-4 p-6 border-t border-cool-gray-200">
            <InfoText
              label={t("num-of-employees")}
              text={String(employee_count)}
            />
            <InfoText
              label={t("foreign-employees")}
              text={String(foreign_employee_count)}
            />
            <InfoText
              label={t("permanent-employees")}
              text={String(permanent_employee_count)}
            />
            <InfoText
              label={t("contract-employees")}
              text={String(contract_employee_count)}
            />
            <InfoText
              label={t("temporary-employees")}
              text={String(temporary_employee_count)}
            />
            <InfoText
              label={t("contractor-employees")}
              text={String(contractor_count)}
            />
            <InfoText
              label={t("part-time-employees")}
              text={String(part_time_employee_count)}
            />
            <InfoText
              label={t("intern-employees")}
              text={String(intern_count)}
            />
            <InfoText
              label={t("specialist-employees")}
              text={String(specialist_count)}
            />
            <InfoText
              label={t("employee-countries")}
              text={employee_countries}
            />
          </div>
        </>
      )}
      {onEdit && (
        <div className="flex justify-end p-6 border-t border-cool-gray-200">
          <Button onClick={() => onEdit()} variant="outline">
            {t("edit")}
          </Button>
        </div>
      )}
    </Card>
  );
}

export default CompanyCard;
